import * as React from 'react';
import '../AutoSum/_autosum.scss';
import './_associations-by-county.scss';
import thousands from 'thousands';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { Field } from '../../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import {
  calculationComponentProps,
  calculationComponentState,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnTypeGroup,
  Header,
  ColumnValue,
  ColumnValuesSection,
} from '../calculationComponents.types';
import {
  serializeComponent,
  updateField,
  getAsNumber,
  getInitialFieldsFromJsonData,
  valueObject,
  uuidv4,
  getSerializedXmlInputComponent,
  IJsonDataStuctures,
} from '../calculationComponents.helpers';
import EventService from '../calculationComponents.eventservice';
import ValidationService from '../../../services/validation.service';
import { InputError } from 'sos-common/js/components/InputError/InputError';

export interface AssociationsByCountyProps extends calculationComponentProps {
  columnOneHeading: string;
  columnTwoHeading: string;
  columnThreeHeading: string;
  counties: string;
  summaryCell1Id: string;
  summaryCell2Id: string;
  preambleId: string;
}

export interface AssociationsByCountyState extends calculationComponentState {
  fields: Field[][];
  sum1: number;
  sum2: number;
  jsonXmlInput: string;
}

export class AssociationsByCounty extends React.Component<AssociationsByCountyProps, AssociationsByCountyState> {
  private _fieldCount: number;
  private _sumLabel: string = `Totalt antal föreningar`;

  constructor(props: AssociationsByCountyProps) {
    super(props);
    this._fieldCount = 0;

    let initialFields: Field[][] = [];
    const counties = props.counties.split(',');

    counties.forEach((county) => {
      initialFields.push(this.getFieldRow(county));
    });

    let isValid = true;

    if (props.formElementJson) {
      let calculationComponentModel = JSON.parse(props.formElementJson) as calculationComponentModel;
      initialFields = getInitialFieldsFromJsonData(calculationComponentModel, initialFields, this.getFieldRow);

      let validation = ValidationService.validateComponent(initialFields, props.formElementName);
      isValid = validation.componentIsValid;
      initialFields = validation.fieldsClone;
    }

    this.state = {
      jsonFields: '',
      sum1: 0,
      sum2: 0,
      fields: initialFields,
      isValid,
      isTouched: false,
      jsonXmlInput: '',
    };
  }

  componentDidMount() {
    this.summarizeFields();
    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({
        isTouched: true,
      });
    });
  }

  getFieldRow = (county?: string) => {
    const { columnOneHeading, columnTwoHeading, columnThreeHeading } = this.props;
    const id1 = `associationsbycount__${this._fieldCount++}${this.props.formElementName}`;
    const id2 = `associationsbycount__${this._fieldCount++}${this.props.formElementName}`;
    const id3 = `associationsbycount__${this._fieldCount++}${this.props.formElementName}`;

    let c1e = 'Du måste ange ett län';
    let c2e = 'Du måste ange antal regionala föreningar';
    let c3e = 'Du måste ange antal lokala föreningar';
    if (columnOneHeading) c1e = 'Du måste ange ' + columnOneHeading.toLowerCase();
    if (columnTwoHeading) c2e = 'Du måste ange ' + columnTwoHeading.toLowerCase();
    if (columnThreeHeading) c3e = 'Du måste ange ' + columnThreeHeading.toLowerCase();

    return [
      {
        id: id1,
        name: id1,
        value: county,
        isValid: true,
        mandatory: true,
        isReadOnly: true,
        isTouched: false,
        errorMessage: c1e,
      },
      {
        id: id2,
        name: id2,
        value: '0',
        placeholder: 'Ange antal',
        isValid: true,
        mandatory: true,
        isTouched: false,
        errorMessage: c2e,
      },
      {
        id: id3,
        name: id3,
        value: '0',
        placeholder: 'Ange antal',
        isValid: true,
        mandatory: true,
        isTouched: false,
        errorMessage: c3e,
      },
    ] as Field[];
  };

  getSerializedComponent = (fields: Field[][]) => {
    const { columnOneHeading, columnTwoHeading, columnThreeHeading, formElementHeading } = this.props;
    let values = [];
    fields.forEach((field) => {
      values.push([
        valueObject(field[0].value.trim(), true),
        valueObject(field[1].value, true),
        valueObject(field[2].value, true),
      ]);
    });
    if (values.length > 0) {
      return serializeComponent(
        [columnOneHeading, columnTwoHeading, columnThreeHeading],
        values,
        ['Totalt antal föreningar', thousands(this.state.sum1, ' '), thousands(this.state.sum2, ' ')],
        formElementHeading
      );
    }
    return '';
  };

  summarizeFields = () => {
    const { formElementName, formElementHeading } = this.props;
    let sum1 = 0;
    let sum2 = 0;
    this.state.fields.forEach((fieldArr) => {
      sum1 += getAsNumber(fieldArr[1].value);
      sum2 += getAsNumber(fieldArr[2].value);
    });
    this.setState(
      {
        sum1,
        sum2,
      },
      () => {
        EventService.fire(`cell-updated:${this.props.summaryCell1Id}`, { detail: this.state.sum1 });
        EventService.fire(`cell-updated:${this.props.summaryCell2Id}`, { detail: this.state.sum2 });
        this.setState({
          jsonFields: this.getSerializedComponent(this.state.fields),
          jsonXmlInput: getSerializedXmlInputComponent(
            this.state.fields,
            calculationComponentModel2.Create(formElementName, formElementHeading, 'Föreningar per län'),
            this.jsonSeriliazetoMainContentForXmlInput
          ),
        });
      }
    );
  };

  jsonSeriliazetoMainContentForXmlInput = (fields: Field[][], data: IJsonDataStuctures) => {
    data.headerDefinitions.push({ id: 1, value: this.props.columnTwoHeading });
    data.headerDefinitions.push({ id: 2, value: this.props.columnThreeHeading });

    fields.forEach((field) => {
      if (field.length !== 3) throw 'For this component, there should be 3 fields per row';
      const headerValue = field[0].value;
      const value1 = field[1].value;
      const value2 = field[2].value;

      const columnValue1 = { headerId: 1, value: value1 } as ColumnValue;
      const columnvalue2 = { headerId: 2, value: value2 } as ColumnValue;
      const columnValues = [columnValue1, columnvalue2];
      data.columnTypeGroups.push({ headerValue, columnValues });
    });

    const columnValue1 = { headerId: 1, value: thousands(this.state.sum1, ' ') } as ColumnValue;
    const columnValue2 = { headerId: 2, value: thousands(this.state.sum2, ' ') } as ColumnValue;
    const columnValues = [columnValue1, columnValue2];
    const sumGroup = {
      isSum: true,
      columnValues: columnValues,
      headerValue: this._sumLabel,
    } as ColumnTypeGroup;
    data.columnTypeGroups.push(sumGroup);
  };

  validateMe = () => {
    let validation = ValidationService.validateComponent(this.state.fields, this.props.formElementName);
    let isValid = validation.componentIsValid;
    let fields = validation.fieldsClone;
    this.setState(
      {
        fields,
        isValid,
      },
      () => {
        ValidationService.validateStepViaElementName(this.props.formElementName);
      }
    );
  };

  setValue = (field: FormField<any>) => {
    const fieldsClone = this.state.fields.slice();
    const fieldToUpdate = updateField(field, fieldsClone);

    this.setState(
      {
        fields: fieldsClone,
        //isTouched: true
      },
      () => {
        this.validateMe();
        EventService.fire(`cell-updated:${fieldToUpdate.id}`, { detail: fieldToUpdate.value });
        this.summarizeFields();
      }
    );
  };

  renderRows = (row: Field[], index: number) => {
    let readOnlyClass = row[0].isReadOnly ? 'stb-form-readonly' : '';
    const isLastRow = index === this.state.fields.length - 1;
    readOnlyClass += isLastRow ? ' last' : '';
    this.setAriaErrorObjects(row);

    return (
      <React.Fragment key={`${this.props.formElementName}_associationsbycountrow_${index}`}>
        <div className="autosum__row" role="row">
          <div className={`ass-counties__col1 ${readOnlyClass}`} role="cell">
            <TextInputForPrint
              id={row[0].id}
              classNames="autosum__input"
              type="text"
              placeholder={row[0].placeholder}
              value={row[0].value}
              onChange={this.setValue}
              fieldName={row[0].name}
              disabled={row[0].isReadOnly}
              isRequired={row[0].mandatory}
              onBlur={this.onBlur}
              errorAriaAttributes={row[0].errorAriaAttributes}
            />
          </div>

          <div className="ass-counties__col2" role="cell">
            <TextInputForPrint
              id={row[1].id}
              classNames={`right ${this.shouldShowError(row[1]) ? 'has-error' : ''}`}
              type="text"
              placeholder={row[1].placeholder}
              value={row[1].value}
              onChange={this.setValue}
              fieldName={row[1].name}
              actAsNumber={true}
              isRequired={row[1].mandatory}
              label={row[0].value}
              labelClass="hidden"
              ariaLabelHidden={false}
              onBlur={this.onBlur}
              errorAriaAttributes={row[1].errorAriaAttributes}
            />
          </div>

          <div className="ass-counties__col3" role="cell">
            <TextInputForPrint
              id={row[2].id}
              classNames={`right ${this.shouldShowError(row[2]) ? 'has-error' : ''}`}
              type="text"
              placeholder={row[2].placeholder}
              value={row[2].value}
              onChange={this.setValue}
              fieldName={row[2].name}
              actAsNumber={true}
              isRequired={row[2].mandatory}
              label={row[0].value}
              labelClass="hidden"
              ariaLabelHidden={false}
              onBlur={this.onBlur}
              errorAriaAttributes={row[2].errorAriaAttributes}
            />
          </div>
        </div>
        {row.map((field) => {
          if (this.shouldShowError(field) && field.errorAriaAttributes) {
            return (
              <InputError
                key={`${field.id}_cError`}
                id={field.errorAriaAttributes['aria-describedby']}
                errorText={field.errorMessage}
              />
            );
          }
        })}
      </React.Fragment>
    );
  };

  setAriaErrorObjects = (fieldRow: Field[]) => {
    fieldRow.forEach((field) => {
      field.errorAriaAttributes = null;
      if (this.shouldShowError(field)) {
        field.errorAriaAttributes = { 'aria-invalid': true, 'aria-describedby': uuidv4() };
      }
    });
  };

  shouldShowError = (field: Field) => {
    return (this.state.isTouched || field.isTouched) && !field.isValid;
  };

  onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { fields } = this.state;
    const clone = fields.slice();
    let targetField: Field = null;
    clone.forEach((field) => {
      let foundField = field.find((f) => f.id === e.target.id);
      if (foundField) targetField = foundField;
    });
    if (targetField) {
      targetField.isTouched = true;
      this.setState({ fields: clone });
    }
  };

  public render() {
    const {
      columnOneHeading,
      columnTwoHeading,
      columnThreeHeading,
      summaryCell1Id,
      summaryCell2Id,
      formElementHeading,
      preambleId,
    } = this.props;
    const { fields, sum1, sum2 } = this.state;
    return (
      <div
        className="cc-component"
        role="table"
        aria-label={`${formElementHeading}`}
        aria-describedby={`${preambleId}`}>
        <div role="rowgroup">
          <div className="autosum__row" role="row">
            <div className="ass-counties__col1" role="columnheader">
              <p className="like-a-label">{columnOneHeading}</p>
            </div>

            <div className="ass-counties__col2" role="columnheader">
              <p className="like-a-label">{columnTwoHeading}</p>
            </div>

            <div className="ass-counties__col3" role="columnheader">
              <p className="like-a-label">{columnThreeHeading}</p>
            </div>
          </div>
        </div>

        <div role="rowgroup">{fields && fields.map(this.renderRows)}</div>

        <div className="autosum__summary" role="row">
          <div className="ass-counties__col1" role="cell">
            <span className="like-a-label">{this._sumLabel}</span>
          </div>
          <div className="ass-counties__col2" role="cell">
            <input
              className="like-a-label autosum__sumfield"
              id={summaryCell1Id}
              readOnly={true}
              type="text"
              value={thousands(sum1, ' ')}
            />
          </div>
          <div className="ass-counties__col3" role="cell">
            <input
              className="like-a-label autosum__sumfield"
              id={summaryCell2Id}
              readOnly={true}
              type="text"
              value={thousands(sum2, ' ')}
            />
          </div>
        </div>
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
